import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';


const TIER_COLORS = {
  viral: '#ef4444', strong: '#f59e0b', solid: '#22c55e', average: '#555', weak: '#333'
};

function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function SubredditDetailPanel({ subredditId, creatorId, accountId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [panelTimeframe, setPanelTimeframe] = useState('30d');

  useEffect(() => {
    if (!subredditId || !creatorId) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ subreddit_id: subredditId, creator_id: creatorId });
    if (accountId) params.append('account_id', accountId);

    const from = new Date();
    if (panelTimeframe === '7d') from.setDate(from.getDate() - 7);
    else if (panelTimeframe === '30d') from.setDate(from.getDate() - 30);
    else if (panelTimeframe === '90d') from.setDate(from.getDate() - 90);
    else if (panelTimeframe === '180d') from.setDate(from.getDate() - 180);
    params.append('from', from.toISOString());

    // Ignore a late response once the sub / timeframe has changed
    let alive = true;
    axios.get(`${API}/api/analytics/subreddit-detail?${params}`)
      .then(res => { if (!alive) return; setData(res.data); setLoading(false); })
      .catch(err => { if (!alive) return; const e = err.response?.data?.error; setError(typeof e === 'string' ? e : err.message); setLoading(false); });
    return () => { alive = false; };
  }, [subredditId, creatorId, accountId, panelTimeframe]);

  if (loading) return <div style={{ padding: '20px', textAlign: 'center', color: '#666', background: '#0f0f0f', borderRadius: '10px', border: '1px solid #2a2a2a', margin: '4px 0 8px' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444', background: '#0f0f0f', borderRadius: '10px', border: '1px solid #2a2a2a', margin: '4px 0 8px' }}>Error: {error}</div>;
  if (!data || !data.posts || data.posts.length === 0) return <div style={{ padding: '20px', textAlign: 'center', color: '#666', background: '#0f0f0f', borderRadius: '10px', border: '1px solid #2a2a2a', margin: '4px 0 8px' }}>No data for this timeframe.</div>;

  const { metrics, posts } = data;

  // Build date-based timeline: every day from first to last post
  const firstDate = new Date(posts[0].posted_at);
  const lastDate = new Date(posts[posts.length - 1].posted_at);
  firstDate.setHours(0, 0, 0, 0);
  lastDate.setHours(0, 0, 0, 0);

  const days = [];
  const d = new Date(firstDate);
  while (d <= lastDate) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }

  // Map posts to days
  const postsByDay = {};
  for (const post of posts) {
    const dayKey = new Date(post.posted_at).toISOString().split('T')[0];
    if (!postsByDay[dayKey]) postsByDay[dayKey] = [];
    postsByDay[dayKey].push(post);
  }

  // Build chart data: for each day, take the best post (or null)
  const chartData = days.map(day => {
    const key = day.toISOString().split('T')[0];
    const dayPosts = postsByDay[key];
    if (!dayPosts || dayPosts.length === 0) return { date: day, post: null };
    // If multiple posts on same day, take the one with most views
    const best = dayPosts.reduce((a, b) => (a.views > b.views ? a : b));
    return { date: day, post: best, count: dayPosts.length };
  });

  const maxViews = Math.max(...chartData.filter(d => d.post).map(d => d.post.views), 1);

  const pillStyle = (active) => ({
    padding: '3px 10px', borderRadius: '12px', border: 'none', cursor: 'pointer',
    fontSize: '11px', fontWeight: '600',
    background: active ? '#ff4500' : 'transparent',
    color: active ? '#fff' : '#666'
  });

  const trendInfo = { rising: { color: '#22c55e', label: 'rising' }, declining: { color: '#ef4444', label: 'declining' }, stable: { color: '#666', label: 'stable' } };
  const trend = trendInfo[metrics.trend] || trendInfo.stable;

  // Tier distribution bar widths
  const tierOrder = ['viral', 'strong', 'solid', 'average', 'weak'];
  // tiers exist for posts with a views reading (removed posts have none)
  const totalPosts = metrics.posts_with_views ?? posts.length;

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '10px', overflow: 'hidden', margin: '4px 0 8px' }}>

      {/* Header with metrics + timeframe selector */}
      <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1a1a1a', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#ff4500' }}>{data.subreddit}</span>
          <span style={{ fontSize: '11px', background: metrics.post_count >= 10 ? '#0a2a0a' : metrics.post_count >= 3 ? '#3a2a00' : '#2a2a2a', color: metrics.post_count >= 10 ? '#22c55e' : metrics.post_count >= 3 ? '#f59e0b' : '#666', padding: '1px 7px', borderRadius: '8px' }}>
            {metrics.post_count >= 10 ? 'Reliable' : metrics.post_count >= 3 ? 'Early' : 'New'}
          </span>
          <span style={{ color: trend.color, fontSize: '11px' }}>{metrics.trend}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '2px', background: '#1a1a1a', borderRadius: '14px', padding: '2px' }}>
            {['7d', '30d', '90d', '180d'].map(tf => (
              <button key={tf} onClick={() => setPanelTimeframe(tf)} style={pillStyle(panelTimeframe === tf)}>{tf}</button>
            ))}
          </div>
          {onClose && <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '14px', cursor: 'pointer', marginLeft: '4px' }}>✕</button>}
        </div>
      </div>

      {/* Metrics row */}
      <div style={{ display: 'flex', gap: '16px', padding: '10px 14px', borderBottom: '1px solid #1a1a1a', fontSize: '13px', color: '#888', flexWrap: 'wrap' }}>
        <span>{metrics.post_count} posts</span>
        <span>{formatNumber(data.weekly_visitors)} weekly visitors</span>
        <span>avg <span style={{ color: '#fff' }}>{formatNumber(metrics.avg_organic)}</span> organic</span>
        {metrics.avg_penetration !== null && <span><span style={{ color: metrics.avg_penetration >= 2 ? '#22c55e' : metrics.avg_penetration >= 0.5 ? '#f59e0b' : '#888' }}>{metrics.avg_penetration}%</span> penetration</span>}
        {metrics.avg_velocity !== null && <span>{metrics.avg_velocity}/h velocity</span>}
        <span><span style={{ color: metrics.breakout_rate >= 30 ? '#22c55e' : metrics.breakout_rate > 0 ? '#f59e0b' : '#555' }}>{metrics.breakout_rate}%</span> breakout</span>
      </div>

      {/* Chart: date-based timeline */}
      <div style={{ padding: '10px 14px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '130px' }}>
          {chartData.map((item, i) => {
            const showLabel = item.post || days.length <= 31;
            const dayLabel = `${item.date.getDate()}/${item.date.getMonth() + 1}`;

            if (!item.post) {
              // Empty day
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%', minWidth: 0 }}>
                  <div style={{ width: '100%', maxWidth: '24px', height: '1px', background: '#1a1a1a', margin: '0 auto' }} />
                  {showLabel && <div style={{ fontSize: '8px', color: '#333', marginTop: '3px' }}>{dayLabel}</div>}
                  {!showLabel && <div style={{ height: '11px' }} />}
                </div>
              );
            }

            const heightPct = Math.max((item.post.views / maxViews) * 100, 4);
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%', minWidth: 0 }}>
                {days.length <= 40 && (
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px', whiteSpace: 'nowrap', lineHeight: '1.2', textAlign: 'center' }}>
                    {formatNumber(item.post.views)}
                    {item.post.penetration !== null && (
                      <div style={{ fontSize: '10px', color: item.post.penetration >= 2 ? '#22c55e' : item.post.penetration >= 0.5 ? '#f59e0b' : '#555' }}>{item.post.penetration}%</div>
                    )}
                  </div>
                )}
                <div
                  title={`${dayLabel}: ${item.post.views.toLocaleString()} views · ${item.post.removed ? 'removed' : `${(item.post.organic_views ?? 0).toLocaleString()} organic`} · ▲${item.post.upvotes} · ${item.post.tier_label}${item.count > 1 ? ' (+' + (item.count - 1) + ' more)' : ''}`}
                  style={{
                    width: '100%', maxWidth: '24px',
                    height: `${heightPct}%`,
                    background: TIER_COLORS[item.post.tier] || '#555',
                    borderRadius: '2px 2px 0 0',
                    cursor: item.post.post_url ? 'pointer' : 'default',
                    opacity: 0.85,
                    transition: 'opacity 0.1s'
                  }}
                  onClick={() => { if (item.post.post_url) window.open(item.post.post_url, '_blank'); }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.85'; }}
                />
                {showLabel && <div style={{ fontSize: '10px', color: '#555', marginTop: '3px' }}>{dayLabel}</div>}
                {!showLabel && <div style={{ height: '13px' }} />}
              </div>
            );
          })}
        </div>

        {/* Legend + tier distribution */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            {tierOrder.filter(t => (metrics.tier_distribution[t] || 0) > 0).map(t => (
              <span key={t} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '1px', background: TIER_COLORS[t], display: 'inline-block' }} />
                <span style={{ color: '#666' }}>{metrics.tier_distribution[t]} {t}</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', height: '10px', width: '100px', borderRadius: '3px', overflow: 'hidden' }}>
            {tierOrder.map(t => {
              const count = metrics.tier_distribution[t] || 0;
              if (count === 0) return null;
              return <div key={t} style={{ width: `${(count / totalPosts) * 100}%`, background: TIER_COLORS[t] }} />;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubredditDetailPanel;
